import { useCallback } from "react";

import { useCreateFavoriteProduct } from "@/Apollo/hooks/product/useCreateFavoriteProduct";
import { useRemoveFavoriteProduct } from "@/Apollo/hooks/product/useRemoveFavoriteProduct";
import { ContentType } from "@/Apollo/schema";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import {
  getCreateFavoriteProductOptimisticResponse,
  getRemoveFavoriteProductOptimisticResponse,
} from "@/common/utils/mutation.utils";
import type { SearchResponse } from "@/features/search/api/queries";
import { SEARCH_RESULTS_QUERY_KEY } from "@/features/search/api/route";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { toAnalyticsProduct } from "@/features/search/utils/analytics.utils";
import { writeProductFavorite } from "@/features/search/utils/favorite-cache.utils";
import type { ApolloCache } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";

/*
 * Favoriting still goes through the gateway — the search service only reports state, it never
 * mutates it. So the outcome has to be written back into the caches by hand, keyed on `favoriteId`:
 * the id the gateway hands out on create, and the one `removeFavorite` needs.
 *
 * Two caches, because the card now has two kinds of caller. Search results live in react-query:
 * every page that's been loaded gets updated, not just the one on screen, so an item shows the same
 * heart after you page back or reopen a landing page. Everywhere else — homepage blocks, PDP rows,
 * account favourites — reads its products out of Apollo, so the gateway's own `favorite` field has
 * to be updated there as well, or the heart empties again as soon as the list re-renders.
 */

export type FavoriteToggleOutcome = "added" | "removed" | "failed";

export const useToggleFavorite = (product: ProductCardProduct) => {
  const queryClient = useQueryClient();
  const { id: productId, favoriteId } = product;

  const writeFavoriteId = useCallback(
    (nextFavoriteId: string | null) => {
      queryClient.setQueriesData<SearchResponse>(
        { queryKey: SEARCH_RESULTS_QUERY_KEY },
        previous =>
          previous && {
            ...previous,
            data: previous.data.map(item =>
              item.id === productId
                ? { ...item, favoriteId: nextFavoriteId }
                : item
            ),
          }
      );
    },
    [productId, queryClient]
  );

  const writeApolloFavorite = useCallback(
    (cache: ApolloCache<unknown>, nextFavoriteId: string | null) =>
      writeProductFavorite(cache, productId, nextFavoriteId),
    [productId]
  );

  /*
   * `onError` keeps a failed mutation from rejecting, so `toggle` reads the outcome off the result
   * and undoes its own cache write. The heart snapping back is the whole error signal — no toast.
   */
  const [createFavorite, { loading: creating }] = useCreateFavoriteProduct({
    onError: error => {
      console.error("useToggleFavorite", error.message);
    },
    update: (cache, { data }) => {
      if (data?.createFavorite?.id) {
        writeApolloFavorite(cache, data.createFavorite.id);
      }
    },
    /*
     * Fills the heart immediately on an Apollo-backed list, which the react-query update above
     * can't reach. The made-up id is fine here: Apollo throws this write away and redoes it with
     * the real id the moment the gateway answers, and the button is disabled in the meantime, so
     * nobody can click remove while the fake id is in the cache.
     */
    optimisticResponse: ({ input }) =>
      getCreateFavoriteProductOptimisticResponse(
        input.object_id,
        `favorite-id-${input.object_id}-${input.content_type}`,
        new Date()
      ),
  });

  const [removeFavorite, { loading: removing }] = useRemoveFavoriteProduct({
    onError: error => {
      console.error("useToggleFavorite", error.message);
    },
    update: (cache, { data }) => {
      if (data?.removeFavorite) {
        writeApolloFavorite(cache, null);
      }
    },
    optimisticResponse: getRemoveFavoriteProductOptimisticResponse(),
  });

  /** WHO-6629: resolves with what happened, so a Pro Buyer's heart can open the project menu after a save. */
  const toggle = useCallback(async (): Promise<FavoriteToggleOutcome> => {
    if (favoriteId) {
      /* Empty the heart now; put the id back if the gateway refuses. */
      writeFavoriteId(null);

      const { data } = await removeFavorite({ variables: { id: favoriteId } });

      if (!data?.removeFavorite) {
        writeFavoriteId(favoriteId);

        return "failed";
      }

      return "removed";
    }

    /*
     * Nothing is written to the cache up front here: `favoriteId` *is* the id `removeFavorite` will
     * need, so a placeholder would be a lie the next click acts on. Callers fill the heart from
     * `isFavoriting` in the meantime, and `isToggling` stops a double-click firing a second create.
     */
    const { data } = await createFavorite({
      variables: {
        input: { object_id: productId, content_type: ContentType.Product },
      },
    });

    if (data?.createFavorite?.id) {
      writeFavoriteId(data.createFavorite.id);
      /* Adds only — `add_to_wishlist` has no removal counterpart. Fired after the gateway confirms,
       * like the gateway PLP's `onCompleted`, so a rejected create reports nothing. */
      analyticsService.trackAddToWishlist(toAnalyticsProduct(product));

      return "added";
    }

    return "failed";
  }, [
    createFavorite,
    favoriteId,
    product,
    productId,
    removeFavorite,
    writeFavoriteId,
  ]);

  /*
   * `isFavoriting` is the optimism the cache cannot express — a create in flight. Removal needs no
   * counterpart: it empties the cache entry immediately and restores it only on failure.
   */
  return { toggle, isToggling: creating || removing, isFavoriting: creating };
};
